import { memo, useCallback } from 'react';
import { Button, Card, Tooltip } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { studioDescriptionKey } from '../../utils/catalogText';
import { ChevronDown, ChevronRight, Pencil } from 'lucide-react';

import { PERMISSIONS } from '../../constants/permissions';
import Can from '../guards/Can';
import StatusBadge from '../shared/StatusBadge';
import StudioIcon from './StudioIcon';
import StudioRowEditor from './StudioRowEditor';
import StudioRowExpanded from './StudioRowExpanded';
import StudioSourceTags from './StudioSourceTags';

/**
 * One studio of the member: icon, name, description, status, source tags, Edit and the expand
 * chevron; below it the read view or, while editing, the scoped editor.
 *
 * @param {object} props
 * @param {object} props.studio UI studio (see `useMemberStudios`)
 * @param {string} props.memberName
 * @param {boolean} props.isOpen
 * @param {boolean} props.isEditing
 * @param {(studioId: string) => void} props.onToggleOpen
 * @param {(studioId: string) => void} props.onEdit
 * @param {() => void} props.onStopEditing
 * @param {(studioId: string, grants: object[]) => Promise<boolean>} props.onSave
 * @param {(studioId: string) => void} props.onRemoveAccess
 */
const StudioRow = ({ studio, memberName, isOpen, isEditing, onToggleOpen, onEdit, onStopEditing, onSave, onRemoveAccess }) => {
  const { t } = useTranslation('people-access');
  // Admin / Owner cannot be edited but their read view lists what the type grants.
  const hasContent = studio.canEdit || studio.grants.length > 0;
  // The whole header toggles the row (like the prototype); the buttons inside stop the click so
  // it is not handled twice. The chevron stays the keyboard way to expand.
  const handleToggle = useCallback(() => onToggleOpen(studio.id), [onToggleOpen, studio.id]);
  const handleChevron = useCallback(
    (event) => {
      event.stopPropagation();
      onToggleOpen(studio.id);
    },
    [onToggleOpen, studio.id],
  );
  const handleEdit = useCallback(
    (event) => {
      event.stopPropagation();
      onEdit(studio.id);
    },
    [onEdit, studio.id],
  );

  return (
    <Card className="p-4" data-testid={`studio-row-${studio.id}`}>
      {/* A mouse shortcut: the chevron button is the accessible control, so no role here. */}
      <div
        className={['flex flex-wrap items-center gap-x-3 gap-y-2', hasContent ? 'cursor-pointer' : '']
          .filter(Boolean)
          .join(' ')}
        onClick={hasContent ? handleToggle : undefined}
      >
        <StudioIcon icon={studio.icon} />
        <div className="min-w-0 flex-1 basis-40">
          <h3 className="text-sm font-semibold text-content">{studio.name}</h3>
          <p className="truncate text-xs text-content-muted">{t(studioDescriptionKey(studio.id), { defaultValue: studio.description })}</p>
        </div>
        {/* On a narrow screen the tags and buttons drop to their own line under the name. */}
        <div className="flex w-full flex-wrap items-center justify-start gap-2 sm:w-auto sm:shrink-0 sm:justify-end">
          <StatusBadge status={studio.status} />
          <StudioSourceTags sources={studio.sources} />
          {studio.canEdit && !isEditing ? (
            <Can permission={PERMISSIONS.MEMBERS_MANAGE_ACCESS}>
              {studio.isSuspended ? (
                <Tooltip content={t('studios.suspended.editDisabled')} triggerClassName="inline-flex">
                  <Button variant="secondary" size="sm" leftIcon={Pencil} disabled>
                    {t('studios.edit')}
                  </Button>
                </Tooltip>
              ) : (
                <Button variant="secondary" size="sm" leftIcon={Pencil} onClick={handleEdit}>
                  {t('studios.edit')}
                </Button>
              )}
            </Can>
          ) : null}
          {hasContent ? (
            <Button
              variant="ghost"
              size="sm"
              leftIcon={isOpen ? ChevronDown : ChevronRight}
              aria-expanded={isOpen}
              aria-label={t(isOpen ? 'studios.collapse' : 'studios.expand', { name: studio.name })}
              onClick={handleChevron}
            />
          ) : null}
        </div>
      </div>
      {isOpen && hasContent ? (
        <div className="mt-4 border-t border-edge pt-4">
          {isEditing ? (
            <StudioRowEditor
              studio={studio}
              memberName={memberName}
              onSave={onSave}
              onClose={onStopEditing}
              onRemoveAccess={onRemoveAccess}
            />
          ) : (
            <StudioRowExpanded studio={studio} memberName={memberName} />
          )}
        </div>
      ) : null}
    </Card>
  );
};

export default memo(StudioRow);
